/**
 * useBreakGlass — houdt bij tot welke leerlingen deze beheerder nu toegang
 * heeft.
 *
 * Eén hook op paginaniveau in plaats van per component, omdat de grant een
 * eigenschap is van de SESSIE en niet van een scherm. Zou elk scherm zijn
 * eigen status ophalen, dan kan het detailpaneel "toegang" zeggen terwijl de
 * tabel eronder nog maskeert — en dan gelooft niemand meer wat er staat.
 *
 * De set bevat alleen leerlingen met een ACTIEF venster. Verloopt een grant,
 * dan valt hij er vanzelf uit bij de volgende controle en sluit de weergave
 * weer dicht.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { requestDataAccess, revokeDataAccess, type AccessGrant } from '../../../data/supabase/dataAccess';
export function useBreakGlass() {
  const [grants, setGrants] = useState<Record<string, AccessGrant>>({});
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // Ruim de timers op bij unmount. Zonder dit blijft er na het verlaten van
  // de pagina een timer draaien die state zet op een component die weg is.
  useEffect(() => {
    const running = timers.current;
    return () => {
      Object.values(running).forEach(clearTimeout);
    };
  }, []);
  const drop = useCallback((learnerId: string) => {
    setGrants(prev => {
      const next = {
        ...prev
      };
      delete next[learnerId];
      return next;
    });
    const t = timers.current[learnerId];
    if (t) {
      clearTimeout(t);
      delete timers.current[learnerId];
    }
  }, []);
  const open = useCallback(async (learnerId: string, reason: string) => {
    const res = await requestDataAccess(learnerId, reason);
    if (!res.ok || !res.grant) return res;
    const grant = res.grant;
    setGrants(prev => ({
      ...prev,
      [learnerId]: grant
    }));

    // Zelf dichtklappen zodra het venster verloopt, in plaats van te wachten
    // tot de beheerder toevallig iets aanklikt. Anders blijft er data op het
    // scherm staan waar de database inmiddels nee tegen zou zeggen.
    const ms = Math.max(0, grant.expiresAt - Date.now());
    timers.current[learnerId] = setTimeout(() => drop(learnerId), ms);
    return res;
  }, [drop]);
  const close = useCallback(async (learnerId: string) => {
    const grant = grants[learnerId];
    if (!grant) return;
    // Lokaal meteen dichtdoen, daarna pas de server. Andersom zou een trage
    // verbinding betekenen dat de gegevens nog even zichtbaar blijven nadat
    // de beheerder op "sluiten" heeft gedrukt.
    drop(learnerId);
    await revokeDataAccess(grant.id);
  }, [grants, drop]);
  const has = useCallback((learnerId: string) => {
    const g = grants[learnerId];
    return !!g && g.expiresAt > Date.now();
  }, [grants]);
  return {
    grants,
    open,
    close,
    has
  };
}